import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Container,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Divider,
  useTheme,
  Tooltip,
  Button,
  Slide,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import StaffDetailsDialog from "./StaffListDialogs/StaffDetailsDialogs/StaffDetailsDialog";
import SendReminderButton from "./components/SendReminderButton";
import BulkActionBar from "./components/BulkActionBar";
import ReminderConfirmationDialog from "./components/ReminderConfirmationDialog";
import BulkScopeDialog from "./StaffListDialogs/BulkOps/BulkScopeDialog";
import ImportExcelDialog from "./StaffListDialogs/AddStaffDialogs/ImportExcelDialog";
import AddEmployeeDialog from "./StaffListDialogs/EmployeeCRUD/AddEmployeeDialog";
import AddEmployeeChoiceDialog from "./StaffListDialogs/AddStaffDialogs/AddEmployeeChoiceDialog";
import { exportStaffToExcel } from "./utils/exportToExcel";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../../components/InformationStepsCard";
import BlinkingDot from "../../../../components/BlinkingDot/BlinkingDot";
import ScopeActionButton from "../../../../components/ScopeActionButton/ScopeActionButton";
import {
  GET_PEOPLE_CHECKS,
  GET_EMPLOYEE_CHECK_STATUSES,
  SEND_REMINDER,
} from "../../../../apis/organization-apis/people-apis/peopleApis";
import {
  CheckStatus,
  CheckCategory,
  statusFilterOptions,
  EmployeeSyncStatus,
  MasterLookupItemsType,
  AccountCategoryKey,
} from "../../../../utils/enums";
import { formatDate as formatDateUtil } from "../../../../utils/dateUtils";
import { CheckStatusChip } from "../../../../components/CheckStatusChip/CheckStatusChip";
import { useStaffFilters } from "./hooks/useStaffFilters";
import StaffFilters from "./components/StaffFilters";
import AccessControl, { FEATURE_IDS } from "../../../../components/AccessControl";

const StaffList = ({
  data,
  handleDateDifference,
  refetch,
  loading,
  isTruto,
  hasHrmsIntegration = false,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffDetails, setStafDetails] = useState([]);
  const [isLoadingChecks, setIsLoadingChecks] = useState(false);
  const [tableFilters, setTableFilters] = useState({});
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [reminderType, setReminderType] = useState(null); // 'policy' or 'training'
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [addEmployeeChoiceDialogOpen, setAddEmployeeChoiceDialogOpen] = useState(false);
  const [showInScope, setShowInScope] = useState(true);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Bulk selection & dialogs
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkScope, setBulkScope] = useState({ open: false, mode: "out" });
  const [bulkReminderType, setBulkReminderType] = useState(null);

  const filters = useStaffFilters();
  const isInitialMount = useRef(true);

  const accountCategoryOptions = [
    { key: AccountCategoryKey.NON_PERSON, title: 'Non Person / Not in Scope' },
    { key: AccountCategoryKey.OFFBOARDED_USER, title: 'Offboarded User' }
  ];

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setIsFilterLoading(true);
    setSelectedIds(new Set());
    const filter = {
      in_scope: showInScope,
    };
    if (!showInScope && filters.selectedAccountCategory) {
      filter.employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
      filter.employee_key = filters.selectedAccountCategory;
    }
    refetch({ filter })
      .then(() => {
        setIsFilterLoading(false);
      })
      .catch(() => {
        setIsFilterLoading(false);
      });
  }, [showInScope, filters.selectedAccountCategory, refetch]);

  const policyFilterOptions = useMemo(() => {
    return [
      { id: "all_accepted", label: "All Accepted" },
      { id: "incomplete", label: "Incomplete" },
    ];
  }, []);

  const roleFilterOptions = useMemo(() => {
    const roles = new Set();
    (staffDetails || []).forEach((s) => {
      const role = s?.role;
      if (role && role !== "-") roles.add(role);
    });
    return Array.from(roles)
      .sort((a, b) => a.localeCompare(b))
      .map((role) => ({ id: role, label: role }));
  }, [staffDetails]);

  const selectColumn = [
    {
      label: "",
      field: "select",
      width: "4%",
      align: "left",
    },
  ];

  const baseColumns = [
    {
      label: "Person",
      field: "person",
      width: showInScope ? "18%" : "25%",
      align: "left",
    },
    {
      label: "Role",
      field: "role",
      width: showInScope ? "10%" : "15%",
      align: "left",
      filterConfig: {
        filterOptions: roleFilterOptions,
      },
    },
  ];

  const checkColumns = [
    {
      label: "Policies Accepted",
      field: "isAcceptedAllPolicies",
      width: "12%",
      align: "left",
      filterConfig: {
        filterOptions: policyFilterOptions,
      },
    },
    {
      label: "Background Check",
      field: "backgroundCheck",
      width: "13%",
      align: "left",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
    {
      label: "Security Training",
      field: "securityTraining",
      width: "12%",
      align: "left",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
    {
      label: "Performance Review",
      field: "performanceReview",
      width: "13%",
      align: "left",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
    {
      label: "Job Evaluation",
      field: "jobEvaluation",
      width: "12%",
      align: "left",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
  ];

  const reasonColumn = [
    {
      label: "Account Type",
      field: "accountType",
      width: "18%",
      align: "left",
    },
    {
      label: "Reason for Inactivity",
      field: "scopeReason",
      width: "22%",
      align: "left",
    },
    {
      label: "Termination Date",
      field: "terminationDate",
      width: "15%",
      align: "left",
    },
  ];

  const actionColumn = [
    {
      label: "",
      field: "action",
      width: "6%",
      align: "left",
    },
  ];

  const columns = useMemo(() => {
    if (showInScope) {
      return [...selectColumn, ...baseColumns, ...checkColumns, ...actionColumn];
    } else {
      return [...selectColumn, ...baseColumns, ...reasonColumn, ...actionColumn];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInScope, roleFilterOptions]);

  const { data: checksData, loading: checksLoading } = useQuery(
    GET_PEOPLE_CHECKS,
    {
      variables: {
        active_check_category: CheckCategory?.POPULATION_CHECK,
      },
    }
  );

  const { data: employeeCheckStatuses, Loading: employeeCheckStatusesLoading } =
    useQuery(GET_EMPLOYEE_CHECK_STATUSES);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const [sendReminder] = useMutation(SEND_REMINDER, {
    onCompleted: (data) => {
      setIsSendingReminder(false);
      setShowConfirmDialog(false);
      setReminderType(null);
      setSnackbarInfo({
        message: "Reminders will be sent to the employees shortly.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    },
    onError: (error) => {
      setIsSendingReminder(false);
      console.error("Error sending reminder:", error);
    },
  });

  const handleManger = useCallback(
    (manager) => {
      let employee =
        data?.getEmployees?.data?.map((item) => ({
          name: item?.name,
          employeeId: item?.employee_id,
        })) || [];

      return employee?.filter(
        (item) => manager && item?.employeeId === manager?.id
      );
    },
    [data]
  );

  // Filter staff based on search query and table filters
  const filteredStaffDetails = useMemo(() => {
    let filtered = staffDetails;

    // Apply search query filter
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        (staff) =>
          staff?.name?.toLowerCase()?.includes(searchLower) ||
          staff?.email?.toLowerCase()?.includes(searchLower) ||
          staff?.role?.toLowerCase()?.includes(searchLower) ||
          (Array?.isArray(staff?.manager)
            ? staff?.manager?.join(", ")?.toLowerCase()
            : staff?.manager?.toLowerCase()
          )?.includes(searchLower)
      );
    }

    // Apply table filters
    return filtered?.filter((staff) => {
      const matchesPoliciesAccepted =
        !tableFilters.isAcceptedAllPolicies ||
        tableFilters.isAcceptedAllPolicies.length === 0 ||
        (() => {
          const policies = staff.policiesAccepted;
          if (!policies || policies.total === 0) {
            return tableFilters.isAcceptedAllPolicies.includes("incomplete");
          }
          if (policies.allAccepted) {
            return tableFilters.isAcceptedAllPolicies.includes("all_accepted");
          } else {
            return tableFilters.isAcceptedAllPolicies.includes("incomplete");
          }
        })();

      const matchesBackgroundCheck =
        !tableFilters.backgroundCheck ||
        tableFilters.backgroundCheck.length === 0 ||
        (staff.backgroundCheck &&
          tableFilters.backgroundCheck.includes(staff.backgroundCheck.status));

      const matchesPerformanceReview =
        !tableFilters.performanceReview ||
        tableFilters.performanceReview.length === 0 ||
        (staff.performanceReview &&
          tableFilters.performanceReview.includes(
            staff.performanceReview.status
          ));

      const matchesSecurityTraining =
        !tableFilters.securityTraining ||
        tableFilters.securityTraining.length === 0 ||
        (staff.securityTraining &&
          tableFilters.securityTraining.includes(
            staff.securityTraining.status
          ));

      const matchesJobEvaluation =
        !tableFilters.jobEvaluation ||
        tableFilters.jobEvaluation.length === 0 ||
        (staff.jobEvaluation &&
          tableFilters.jobEvaluation.includes(staff.jobEvaluation.status));

      const matchesRole =
        !tableFilters.role ||
        tableFilters.role.length === 0 ||
        tableFilters.role.includes(staff.role);

      return (
        matchesPoliciesAccepted &&
        matchesBackgroundCheck &&
        matchesPerformanceReview &&
        matchesSecurityTraining &&
        matchesJobEvaluation &&
        matchesRole
      );
    });
  }, [staffDetails, filters.searchQuery, tableFilters]);

  const handleFilterChange = (filters) => {
    setTableFilters(filters);
    setSelectedIds(new Set());
  };

  // ---- Bulk selection ----
  const handleSelectRow = useCallback((employeeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(employeeId) ? next.delete(employeeId) : next.add(employeeId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        setSelectedIds(
          new Set((filteredStaffDetails || []).map((s) => s.employeeId))
        );
      } else {
        setSelectedIds(new Set());
      }
    },
    [filteredStaffDetails]
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Resolve selection -> original employee objects (carry id / work_email / start_date)
  const selectedEmployees = useMemo(
    () =>
      (filteredStaffDetails || [])
        .filter((s) => selectedIds.has(s.employeeId))
        .map((s) => s._originalEmployee || s),
    [filteredStaffDetails, selectedIds]
  );

  const handleBulkSuccess = useCallback(async () => {
    clearSelection();
    const filter = { in_scope: showInScope };
    if (!showInScope && filters.selectedAccountCategory) {
      filter.employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
      filter.employee_key = filters.selectedAccountCategory;
    }
    await refetch({ filter });
  }, [clearSelection, showInScope, filters.selectedAccountCategory, refetch]);

  useEffect(() => {
    if (data?.getEmployees && checksData?.getPeopleChecks) {
      const employees = data?.getEmployees?.data || [];
      setIsLoadingChecks(true);

      // Process each employee
      const processEmployee = async (employee) => {
        if (!employee?.emails?.[0]?.email) return [];

        try {
          const statusData =
            employeeCheckStatuses?.getEmployeeCheckStatuses?.find(
              (status) => status?.employee_id === employee?.emails?.[0]?.email
            );

          if (!statusData) return [];

          return statusData || [];
        } catch (error) {
          console?.error("Error fetching check statuses:", error);
          return [];
        }
      };

      // Update staff details with check statuses
      // TODO: change this, not the right approach
      Promise?.all(
        employees
          ?.map(async (item) => {
            const checkStatuses = await processEmployee(item);
            const assignManager = handleManger(item?.manager);

            return {
              avatar: item?.avatar,
              name: item?.name,
              email: item?.emails?.[0]?.email,
              role: item?.employments?.[0]?.job_title || "-",
              manager:
                assignManager?.length > 0
                  ? assignManager?.map((m) => m?.name)
                  : "-",
              joinDate: item?.start_date || "-",
              passedChecksCount: checkStatuses.passed_checks_count || 0,
              failedChecksCount: checkStatuses.failed_checks_count || 0,
              disabledChecksCount: checkStatuses.disabled_checks_count || 0,
              checkStatuses: checkStatuses?.checks || [],
              policiesAccepted: (() => {
                const policyChecks =
                  checkStatuses?.checks?.filter((check) =>
                    check?.check_type.includes("accepted_by_employees")
                  ) || [];
                // Exclude disabled checks from total
                const nonDisabledChecks = policyChecks.filter(
                  (check) => check?.status !== CheckStatus.DISABLED
                );
                // Count passing checks (excluding disabled)
                const passingCount = nonDisabledChecks.filter(
                  (check) => check?.status === CheckStatus.PASS
                ).length;
                const totalCount = nonDisabledChecks.length;
                return {
                  passing: passingCount,
                  total: totalCount,
                  allAccepted: totalCount > 0 && passingCount === totalCount,
                };
              })(),
              backgroundCheck:
                checkStatuses?.checks?.find((check) =>
                  check?.check_type.includes("background_check_review")
                ) || null,
              performanceReview:
                checkStatuses?.checks?.find((check) =>
                  check?.check_type.includes(
                    "periodic_employee_performance_review"
                  )
                ) || null,
              securityTraining:
                checkStatuses?.checks?.find((check) =>
                  check?.check_type.includes("security_training_completed")
                ) || null,
              jobEvaluation:
                checkStatuses?.checks?.find((check) =>
                  check?.check_type.includes("periodic_job_evaluation")
                ) || null,
              employeeId: item?.employee_id,
              employeeSyncStatus: item?.employee_sync_status || null,
              employeeSyncStatusKey: item?.employee_sync_status?.key || null,
              in_scope: item?.in_scope === true,
              scopeReason: item?.scope_reason || "-",
              accountCategory: item?.account_category || null,
              accountCategoryKey: item?.account_category?.key || null,
              terminationDate: item?.termination_date || null,
              user_fetching_info: item?.user_fetching_info || null,
              is_complete: item?.is_complete ?? true,
              _originalEmployee: item,
            };
          })
      )
        ?.then(setStafDetails)
        ?.finally(() => setIsLoadingChecks(false));
    }
  }, [data, checksData, handleManger, employeeCheckStatuses]);

  const handleOpenDialog = (staff) => {
    setSelectedStaff(staff);
    setOpen(true);
  };

  if (loading || checksLoading || isLoadingChecks) {
    return (
      <Container
        sx={{
          height: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </Container>
    );
  }

  const renderCell = (row, column) => {
    switch (column?.field) {
      case "select":
        return (
          <Checkbox
            size="small"
            checked={selectedIds.has(row.employeeId)}
            onChange={() => handleSelectRow(row.employeeId)}
            onClick={(e) => e.stopPropagation()}
            sx={{ p: 0 }}
          />
        );
      case "person":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
                <Tooltip title={row?.name || ""} arrow>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: theme.customColors.text.strong,
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row?.name}
                  </Typography>
                </Tooltip>
                {/* Orange dot for incomplete fields */}
                {row?.in_scope && row?.is_complete === false && (
                  <BlinkingDot
                    color="#f97316"
                    size={16}
                    dotSize={6}
                    tooltip="This employee has incomplete profile information. Please update their details to complete the profile."
                  />
                )}
              </Box>
              <Tooltip title={row?.email || ""} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    textAlign: "left",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row?.email}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        );
      case "role":
        const hasRole =
          row?.role && row?.role !== "-" && row?.role !== "Not Assigned";
        return (
          <Box sx={{ textAlign: "left", width: "100%", minWidth: 0, display: "flex", alignItems: "center" }}>
            <Tooltip
              title={row?.role === "-" ? "Not Assigned" : row?.role}
              arrow
            >
              <Chip
                label={row?.role === "-" ? "Not Assigned" : row?.role}
                size="small"
                sx={{
                  bgcolor: hasRole
                    ? "rgba(26,86,219,0.08)"
                    : "background.paper",
                  color: hasRole ? "primary.main" : "text.secondary",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  height: "24px",
                  borderRadius: 1,
                  ...(row?.role !== "-" && { px: 1.5 }),
                  boxShadow: "none",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  },
                }}
              />
            </Tooltip>
          </Box>
        );

      case "isAcceptedAllPolicies":
        const policies = row?.policiesAccepted;
        const dotColor = policies.allAccepted
          ? theme.customColors.status.success.text
          : theme.customColors.status.error.text;
        return (
          <Box
            sx={{
              width: "calc(50% + 16px)",
              mx: -1,
              my: -1.5,
              py: 0.75,
              px: 1.5,
              textAlign: "center",
              border: `1px dashed ${policies.allAccepted
                ? theme.customColors.status.success.text
                : theme.customColors.border.main
                }`,
              borderRadius: 1.5,
              backgroundColor: policies.allAccepted
                ? theme.customColors.status.success.bg
                : theme.customColors.background.light,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                mb: 0.5,
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: dotColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                color={
                  policies.allAccepted
                    ? theme.customColors.status.success.text
                    : theme.customColors.text.strong
                }
                sx={{ lineHeight: 1.4 }}
              >
                {policies.passing}/{policies.total}
              </Typography>
            </Box>
          </Box>
        );
      case "backgroundCheck":
        if (!row?.backgroundCheck) {
          return (
            <Box sx={{ textAlign: "left", width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                -
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <CheckStatusChip status={row.backgroundCheck.status} />
          </Box>
        );
      case "performanceReview":
        if (!row?.performanceReview) {
          return (
            <Box sx={{ textAlign: "left", width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                -
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <CheckStatusChip status={row.performanceReview.status} />
          </Box>
        );
      case "securityTraining":
        if (!row?.securityTraining) {
          return (
            <Box sx={{ textAlign: "left", width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                -
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <CheckStatusChip status={row.securityTraining.status} />
          </Box>
        );
      case "jobEvaluation":
        if (!row?.jobEvaluation) {
          return (
            <Box sx={{ textAlign: "left", width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                -
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <CheckStatusChip status={row.jobEvaluation.status} />
          </Box>
        );
      case "accountType":
        const accountTypeKey = row?.accountCategoryKey;
        if (!accountTypeKey) {
          return (
            <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  textAlign: "left",
                }}
              >
                -
              </Typography>
            </Box>
          );
        }

        let chipLabel = "";
        if (accountTypeKey === AccountCategoryKey.OFFBOARDED_USER) {
          chipLabel = "Offboarded User";
        } else if (accountTypeKey === AccountCategoryKey.NON_PERSON) {
          chipLabel = "Non Person / Not in Scope";
        }

        return (
          <Box sx={{ width: "100%", minWidth: 0, display: "flex", alignItems: "center" }}>
            <Chip
              label={chipLabel}
              size="medium"
              sx={{
                borderRadius: "0px",
                backgroundColor: "#1976d214",
                color: "#1976d2",
                fontWeight: 500,
                fontSize: "0.75rem",
                height: "32px",
              }}
            />
          </Box>
        );
      case "scopeReason":
        return (
          <Box sx={{ width: "100%", minWidth: 0, display: "flex", alignItems: "center", height: "32px", px: 1 }}>
            <Tooltip title={row?.scopeReason || "-"} arrow>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.strong,
                  textAlign: "left",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row?.scopeReason || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "terminationDate":
        const formattedDate = formatDateUtil(row?.terminationDate);
        return (
          <Box sx={{ width: "100%", minWidth: 0, display: "flex", alignItems: "left", px: 1.2 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                textAlign: "left",
                width: "100%",
              }}
            >
              {formattedDate || "-"}
            </Typography>
          </Box>
        );
      case "action":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(row)}
                sx={{
                  color: theme.customColors.icons.dark,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.light,
                    transform: "translateX(-2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return row[column?.field];
    }
  };


  const renderHeaderCell = (column) => {
    if (column?.field !== "select") return null; // fall back to default header
    const rows = filteredStaffDetails || [];
    const allSelected = rows.length > 0 && selectedIds.size === rows.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < rows.length;
    return (
      <Checkbox
        size="small"
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        sx={{ p: 0 }}
      />
    );
  };

  const handleSelectReminderType = (type) => {
    setBulkReminderType(null); // org-wide
    setReminderType(type);
    setShowConfirmDialog(true);
  };

  // Bulk reminder for selected employees only.
  const handleBulkReminder = (type) => {
    setBulkReminderType(type);
    setReminderType(type);
    setShowConfirmDialog(true);
  };

  const handleConfirmSendReminder = async () => {
    setIsSendingReminder(true);
    try {
      if (!reminderType) {
        setIsSendingReminder(false);
        setShowConfirmDialog(false);
        return;
      }

      // Map UI values to GraphQL enum values
      const typeMap = {
        policy: "policy_ack_reminder",
        training: "training_reminder",
      };

      // When triggered from a selection, scope the reminder to those employees.
      const employeeIds = bulkReminderType
        ? selectedEmployees
            .map((e) => e.work_email || e.emails?.[0]?.email)
            .filter(Boolean)
        : undefined;

      await sendReminder({
        variables: {
          type: typeMap[reminderType],
          ...(employeeIds ? { options: { employeeIds } } : {}),
        },
      });
      if (bulkReminderType) clearSelection();
    } catch (error) {
      console.error("Error sending reminder:", error);
      setIsSendingReminder(false);
    }
  };

  const handleCancelReminder = () => {
    setShowConfirmDialog(false);
    setReminderType(null);
    setBulkReminderType(null);
  };

  const handleExportToExcel = () => {
    if (filteredStaffDetails?.length > 0) {
      exportStaffToExcel(filteredStaffDetails, { inScope: showInScope });
    }
  };

  const handleBulkExport = () => {
    if (selectedEmployees?.length > 0) {
      exportStaffToExcel(
        filteredStaffDetails.filter((s) => selectedIds.has(s.employeeId)),
        { inScope: showInScope }
      );
    }
  };

  return (
    <Box
      sx={{ width: "100%", pl: 0, pr: 0, ml: 0, mr: 0, mt: 2 }}
      data-id="staff-list-container"
    >
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page helps you manage and track employee-related compliance activities across your organization."
          steps={[
            "**Manage where employees come from:** your people are pulled from your connected employee data sources (HRMS and identity tools like Okta). To add a source, go to the **Integrations** tab, click **Manage** on the relevant integration, and turn on **Use as employee data source**. To set which source takes priority when someone appears in more than one, use **People Source Priority** at the top of this page.",
            "If you don't see the **Use as employee data source** option, contact admin to enable it for you.",
            "**Blinking orange dot indicates that the employee has incomplete fields.**",
            "View the list of all employees and their assigned roles within the organization.",
            "Check policy acknowledgments, background checks, performance reviews, security training, and job evaluations etc status for each employee.",
            "Monitor the progress of employee-related tasks such as background verification, performance evaluations, and onboarding requirements.",
            "Send reminders for pending policies and trainings to employees to ensure they are completed on time."
          ]}
          footerText="💡 Regularly reviewing employee compliance status helps maintain accountability and ensures your organization stays audit-ready."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* Staff List Section */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Staff Members
              </Typography>
              <ToggleButtonGroup
                value={showInScope ? 'in_scope' : 'out_of_scope'}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setShowInScope(newValue === 'in_scope');
                    filters.setSelectedAccountCategory(null);
                  }
                }}
                aria-label="status filter"
                size="small"
                sx={{
                  borderRadius: '26px',
                  border: '1px solid #E8EAED',
                  gap: 0.2,
                  overflow: 'hidden',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '36px',
                    color: '#6B7280',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 3,
                    py: 1,
                    minWidth: '70px',
                    transition: 'all 0.2s ease-in-out',
                    '&.Mui-selected': {
                      backgroundColor: '#3B82F6',
                      color: '#fff',
                      boxShadow: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        backgroundColor: '#2563EB',
                      },
                    },
                    '&:hover:not(.Mui-selected)': {
                      backgroundColor: '#F9FAFB',
                      color: '#374151',
                    },
                  },
                }}
              >
                <ToggleButton value="in_scope">In Scope</ToggleButton>
                <ToggleButton value="out_of_scope">Out of Scope</ToggleButton>
              </ToggleButtonGroup>
              {filters.hasActiveFilters && (
                <Chip
                  label={`${filters.activeFiltersCount} filter${filters.activeFiltersCount !== 1 ? 's' : ''} active`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": {
              fontSize: "16px",
              fontWeight: "bold",
            },
            padding: "14px",
            minHeight: 88,
            boxSizing: "border-box",
            "& .MuiCardHeader-action": {
              m: 0,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
            },
          }}
          action={
            selectedIds.size > 0 ? (
              <BulkActionBar
                count={selectedIds.size}
                inScopeEligibleCount={
                  selectedEmployees.filter((e) => !e.termination_date).length
                }
                showInScope={showInScope}
                onExport={handleBulkExport}
                onSendReminder={handleBulkReminder}
                onMarkOutOfScope={() => setBulkScope({ open: true, mode: "out" })}
                onMarkInScope={() => setBulkScope({ open: true, mode: "in" })}
                onClear={clearSelection}
              />
            ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {showInScope && (
                <Tooltip
                  title={hasHrmsIntegration ? "Employee management is handled automatically through HRMS integration. Manual employee addition is disabled." : "Add Employee"}
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setAddEmployeeChoiceDialogOpen(true)}
                      disabled={hasHrmsIntegration}
                      sx={{
                        bgcolor: theme.customColors.button.primary,
                        color: theme.customColors.button.text,
                        fontWeight: 500,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "14px",
                        px: 2,
                        py: 1,
                        "&:hover": {
                          bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
                        },
                        "&:disabled": {
                          bgcolor: theme.customColors.background.light,
                          color: theme.customColors.text.disabled,
                        },
                      }}
                    >
                      Add Employee
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Download staff data as Excel" arrow>
                <span>
                  <AccessControl featureId={FEATURE_IDS.PEOPLE_STAFF_CHECKS_EXCEL_EXPORT}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                      onClick={handleExportToExcel}
                      disabled={!filteredStaffDetails?.length}
                      sx={{
                        fontWeight: 500,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "14px",
                        px: 2,
                        py: 1,
                        borderColor: theme.customColors.border.main,
                        color: theme.customColors.text.strong,
                        "&:hover": {
                          borderColor: theme.customColors.border.strong,
                          backgroundColor: theme.customColors.background.hover,
                        },
                        "&:disabled": {
                          borderColor: theme.customColors.border.disabled,
                          color: theme.customColors.text.disabled,
                        },
                      }}
                    >
                      Export
                    </Button>
                  </AccessControl>
                </span>
              </Tooltip>
              {showInScope && (
                <SendReminderButton
                  disabled
                  disabledReason="Select employees first to send a reminder"
                  onReminderTypeSelect={handleSelectReminderType}
                />
              )}
              <Tooltip title="Select employees to mark out of scope" arrow>
                <span>
                  <ScopeActionButton
                    mode={showInScope ? "out" : "in"}
                    disabled
                  />
                </span>
              </Tooltip>
            </Box>
            )
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: !showInScope && accountCategoryOptions.length > 0 ? 2 : 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={filters.searchQuery}
                  onChange={(e) => filters.setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: filters.searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => filters.setSearchQuery('')}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      transition: "all 0.2s ease",
                      fontSize: "14px",
                      "& fieldset": {
                        borderColor: "#E5E7EB",
                      },
                      "&:hover fieldset": {
                        borderColor: "#9CA3AF",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 120, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <Box
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: "10px",
                    backgroundColor: "#F3F4F6",
                    border: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="#374151" fontWeight="500" sx={{ lineHeight: 1.2 }}>
                    {filteredStaffDetails?.length}{" "}
                    {filteredStaffDetails?.length === 1 ? "member" : "members"}{" "}
                    {showInScope ? "active" : "inactive"}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {!showInScope && (
              <StaffFilters
                filters={filters}
                accountCategoryOptions={accountCategoryOptions}
              />
            )}
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          <Box>
            <ReusableTable
              columns={columns}
              data={filteredStaffDetails}
              renderCell={renderCell}
              renderHeaderCell={renderHeaderCell}
              onFilterChange={handleFilterChange}
              filters={tableFilters}
              loading={
                loading ||
                checksLoading ||
                isLoadingChecks ||
                employeeCheckStatusesLoading ||
                isFilterLoading
              }
              rowsPerPageOptions={[10, 50, 100]}
              rowsPerPage={50}
              sx={{
                "& .MuiTableCell-root": {
                  py: 1.5,
                  px: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
                "& .MuiTableRow-root:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.01)",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {selectedStaff && (
        <StaffDetailsDialog
          open={open && selectedStaff}
          onClose={() => setOpen(false)}
          staffMember={selectedStaff}
          originalEmployee={selectedStaff._originalEmployee || selectedStaff}
          handleDateDifference={handleDateDifference}
          refetch={refetch}
          setValue={() => { }}
          isTruto={isTruto}
          data={data}
          hasHrmsIntegration={hasHrmsIntegration}
          onStaffMemberUpdate={async (updatedStaffMember) => {
            setSelectedStaff(updatedStaffMember);
            const filter = {
              in_scope: showInScope,
            };
            if (!showInScope && filters.selectedAccountCategory) {
              filter.employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
              filter.employee_key = filters.selectedAccountCategory;
            }
            await refetch({ filter });
          }}
        />
      )}

      <ReminderConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelReminder}
        onConfirm={handleConfirmSendReminder}
        reminderType={reminderType}
        loading={isSendingReminder}
      />

      <AddEmployeeChoiceDialog
        open={addEmployeeChoiceDialogOpen}
        onClose={() => setAddEmployeeChoiceDialogOpen(false)}
        onSelectAddEmployee={() => setAddEmployeeDialogOpen(true)}
        onSelectImportExcel={() => setImportDialogOpen(true)}
        hasHrmsIntegration={hasHrmsIntegration}
      />

      <ImportExcelDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        refetch={refetch}
        onBack={() => {
          setImportDialogOpen(false);
          setAddEmployeeChoiceDialogOpen(true);
        }}
      />

      <AddEmployeeDialog
        open={addEmployeeDialogOpen}
        onClose={() => setAddEmployeeDialogOpen(false)}
        refetch={refetch}
        onBack={() => {
          setAddEmployeeDialogOpen(false);
          setAddEmployeeChoiceDialogOpen(true);
        }}
      />

      <BulkScopeDialog
        open={bulkScope.open}
        mode={bulkScope.mode}
        onClose={() => setBulkScope({ open: false, mode: bulkScope.mode })}
        employees={selectedEmployees}
        onSaveSuccess={handleBulkSuccess}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default StaffList;
